import React, { useMemo } from "react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import PortalChart from "./PortalChart";
import { chartTooltipProps } from "./chartTooltipProps";
import {
  CHART_PALETTE,
  CHART_SERIES,
  areaSeriesProps,
  barSeriesProps,
  chartAxisTick,
  chartGridStroke,
  horizontalBarProps,
  lineSeriesProps,
} from "./chartTheme";

export function CheckinDailyComposedChart({ data, showSales }) {
  const hasSales = showSales && data.some((d) => d.sales);
  return (
    <PortalChart height={280}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
        <XAxis dataKey="date" tick={chartAxisTick} />
        <YAxis allowDecimals={false} tick={chartAxisTick} />
        <Tooltip {...chartTooltipProps()} />
        <Legend />
        <Area dataKey="completed" name="Completed" {...areaSeriesProps(CHART_SERIES.completed, 0)} />
        <Line dataKey="queued" name="Queued" {...lineSeriesProps(CHART_SERIES.queued, 120)} />
        <Line dataKey="in_service" name="In service" {...lineSeriesProps(CHART_SERIES.in_service, 200)} />
        <Bar dataKey="cancelled" name="Cancelled" {...barSeriesProps(CHART_SERIES.cancelled, 280)} />
        {hasSales ? <Line dataKey="sales" name="Sales" {...lineSeriesProps(CHART_SERIES.sales, 360)} /> : null}
      </ComposedChart>
    </PortalChart>
  );
}

export function PeakHoursLineChart({ data }) {
  return (
    <PortalChart height={280}>
      <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
        <XAxis dataKey="label" tick={{ ...chartAxisTick, fontSize: 10 }} />
        <YAxis allowDecimals={false} tick={chartAxisTick} />
        <Tooltip {...chartTooltipProps()} />
        <Line dataKey="count" name="Check-ins" {...lineSeriesProps(CHART_SERIES.primary)} />
      </LineChart>
    </PortalChart>
  );
}

export function DistributionDonutChart({ data, nameKey = "name", valueKey = "count" }) {
  const slices = useMemo(
    () =>
      (data || []).map((row, index) => ({
        name: row[nameKey] ?? row.label ?? row.source ?? "—",
        value: Number(row[valueKey] ?? row.count ?? row.total ?? 0),
        fill: CHART_PALETTE[index % CHART_PALETTE.length],
      })),
    [data, nameKey, valueKey],
  );
  if (!slices.some((s) => s.value > 0)) return null;
  return (
    <PortalChart height={260}>
      <PieChart>
        <Tooltip {...chartTooltipProps()} />
        <Pie
          data={slices}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={58}
          outerRadius={92}
          paddingAngle={2}
          isAnimationActive
          animationDuration={1000}
          animationEasing="ease-out"
        >
          {slices.map((entry) => (
            <Cell key={entry.name} fill={entry.fill} stroke="transparent" />
          ))}
        </Pie>
        <Legend />
      </PieChart>
    </PortalChart>
  );
}

export function HorizontalRankChart({ data, dataKey = "completed", color = CHART_SERIES.primary }) {
  return (
    <PortalChart height={260}>
      <BarChart data={data} layout="vertical" margin={{ left: 4, right: 12 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={chartAxisTick} />
        <YAxis type="category" dataKey="name" width={90} tick={{ ...chartAxisTick, fontSize: 10 }} />
        <Tooltip {...chartTooltipProps()} />
        <Bar dataKey={dataKey} name="Count" {...horizontalBarProps(color)} />
      </BarChart>
    </PortalChart>
  );
}

export function FlowDailyAreaChart({ data }) {
  return (
    <PortalChart height={260}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
        <XAxis dataKey="date" tick={chartAxisTick} />
        <YAxis tick={chartAxisTick} allowDecimals={false} />
        <Tooltip {...chartTooltipProps()} />
        <Legend />
        <Area dataKey="accessed" name="Accessed" {...areaSeriesProps(CHART_SERIES.accessed, 0)} />
        <Area dataKey="completed" name="Completed" {...areaSeriesProps(CHART_SERIES.completed, 150)} />
      </ComposedChart>
    </PortalChart>
  );
}

export function FlowMixDonutChart({ rows, labelFn }) {
  const data = (rows || []).map((r, i) => ({
    name: labelFn ? labelFn(r.flowType) : r.flowType,
    count: Number(r.total ?? 0),
    fill: CHART_PALETTE[i % CHART_PALETTE.length],
  }));
  const chart = <DistributionDonutChart data={data} nameKey="name" valueKey="count" />;
  if (!data.some((d) => d.count > 0)) {
    return <p className="pca-panel__empty">No flow events yet.</p>;
  }
  return chart;
}

export function ServiceDailyComposedChart({ data }) {
  return (
    <PortalChart height={260}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
        <XAxis dataKey="date" tick={chartAxisTick} />
        <YAxis tick={chartAxisTick} allowDecimals={false} />
        <Tooltip {...chartTooltipProps()} />
        <Legend />
        <Bar dataKey="taken" name="Taken" {...barSeriesProps("#38bdf8", 0)} />
        <Line dataKey="completed" name="Completed" {...lineSeriesProps("#34d399", 120)} />
        <Line dataKey="fromAnalysis" name="From analysis" {...lineSeriesProps("#a78bfa", 240)} />
      </ComposedChart>
    </PortalChart>
  );
}

export function StaffDailyLineChart({ data }) {
  return (
    <PortalChart height={260}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
        <XAxis dataKey="date" tick={chartAxisTick} />
        <YAxis tick={chartAxisTick} allowDecimals={false} />
        <Tooltip {...chartTooltipProps()} />
        <Legend />
        <Line dataKey="checkins" name="Check-ins" {...lineSeriesProps(CHART_SERIES.primary, 0)} />
        <Line dataKey="services" name="Services" {...lineSeriesProps(CHART_SERIES.secondary, 120)} />
        <Line dataKey="flowEvents" name="Flow events" {...lineSeriesProps(CHART_SERIES.accent, 240)} />
      </LineChart>
    </PortalChart>
  );
}

export function CustomerSegmentsPieChart({ segments }) {
  const data = (segments || []).map((s, i) => ({
    label: s.label || s.segment || "Guest",
    count: Number(s.count ?? 0),
    fill: CHART_PALETTE[i % CHART_PALETTE.length],
  }));
  return <DistributionDonutChart data={data} nameKey="label" valueKey="count" />;
}

export function CustomerDailyAreaChart({ data }) {
  return (
    <PortalChart height={260}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
        <XAxis dataKey="date" tick={{ ...chartAxisTick, fontSize: 10 }} />
        <YAxis allowDecimals={false} tick={chartAxisTick} />
        <Tooltip {...chartTooltipProps()} />
        <Legend />
        <Area dataKey="checkins" name="Check-ins" {...areaSeriesProps("#67e8f9", 0)} />
        <Line dataKey="unique_guests" name="Unique guests" {...lineSeriesProps("#34d399", 150)} />
      </ComposedChart>
    </PortalChart>
  );
}
